import { useMemo } from "react";

import { flattenMunicipalPropertyPageList } from "@/domain/municipal-property/lib/flatten-municipal-property-page-list";
import { MunicipalPropertyCatalogFilter } from "@/domain/municipal-property/models/municipal-property-catalog-filter.model";
import { MunicipalPropertyListPage } from "@/domain/municipal-property/models/municipal-property-list-page.model";
import { toMunicipalPropertyListInput } from "@/domain/municipal-property/models/to-municipal-property-list-input";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { DEFAULT_STALE_TIME_MS } from "@/services/react-query.service/stale-time";
import { useInfiniteQuery } from "@tanstack/react-query";

import { municipalPropertyListQueryDocument } from "./municipal-property-list-query.gql";

interface UseMunicipalPropertyListProps {
  workspaceId: string;
  filter: MunicipalPropertyCatalogFilter;
  isEnabled?: boolean;
}

export const useMunicipalPropertyList = ({ workspaceId, filter, isEnabled = true }: UseMunicipalPropertyListProps) => {
  const queryResult = useInfiniteQuery({
    queryKey: filter.inventoryId
      ? [...queryKeys.municipalProperties.inventoryCandidates({ workspaceId, inventoryId: filter.inventoryId }), filter]
      : queryKeys.municipalProperties.list({ workspaceId, filter }),
    queryFn: async ({ pageParam }): Promise<MunicipalPropertyListPage> => {
      const client = createOperationClient("MunicipalPropertyList");
      const response = await client.request(municipalPropertyListQueryDocument, {
        input: toMunicipalPropertyListInput({ workspaceId, filter, after: pageParam }),
      });
      return response.municipalPropertyList;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? (lastPage.endCursor ?? undefined) : undefined),
    enabled: isEnabled && !!workspaceId,
    staleTime: DEFAULT_STALE_TIME_MS,
  });

  const { propertyList, totalCount } = useMemo(
    () => flattenMunicipalPropertyPageList(queryResult.data?.pages ?? []),
    [queryResult.data?.pages],
  );

  return {
    propertyList,
    totalCount,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    refetch: queryResult.refetch,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
    isFetchNextPageError: queryResult.isFetchNextPageError,
    fetchNextPage: queryResult.fetchNextPage,
  };
};
